import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, CheckCircle2, XCircle } from 'lucide-react';
import { useBooking, useCancelBooking, useCompleteBooking, useRescheduleBooking } from '../../hooks/useBooking';
import { BookingStatus } from '../../types/booking';
import BookingStatusBadge from '../../components/booking/BookingStatusBadge';
import SyncStatusBadge from '../../components/booking/SyncStatusBadge';
import BookingModal from '../../components/booking/BookingModal';
import Spinner from '../../components/Spinner';

export default function BookingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: booking, isLoading } = useBooking(id ?? '');
  const rescheduleBooking = useRescheduleBooking();
  const cancelBooking = useCancelBooking();
  const completeBooking = useCompleteBooking();
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Spinner size="h-8 w-8" />
        <p className="mt-3 text-strivre-mutedtext">Loading booking details...</p>
      </div>
    );
  }

  if (!booking) {
    return <div className="p-12 text-center text-strivre-mutedtext">Booking not found.</div>;
  }

  const durationMinutes = Math.round((new Date(booking.bookingEndDate).getTime() - new Date(booking.bookingDate).getTime()) / 60000);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/booking/bookings')} className="flex items-center gap-1.5 text-sm text-strivre-mutedtext hover:text-strivre-navy">
        <ArrowLeft size={16} /> Back to Bookings
      </button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-strivre-navy text-left">{booking.bookingReference}</h1>
          <p className="text-sm text-strivre-mutedtext mt-1">
            {booking.serviceName} · {new Date(booking.bookingDate).toLocaleDateString()} at {new Date(booking.bookingDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(booking.status === BookingStatus.Pending || booking.status === BookingStatus.Confirmed) && (
            <button onClick={() => setRescheduleOpen(true)} className="btn-outline btn-sm flex items-center gap-1.5"><CalendarClock size={14} /> Reschedule</button>
          )}
          {booking.status === BookingStatus.Confirmed && (
            <button onClick={() => completeBooking.mutate(booking.id)} className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
              <CheckCircle2 size={14} /> Mark Complete
            </button>
          )}
          {booking.status !== BookingStatus.Cancelled && booking.status !== BookingStatus.Completed && (
            <button
              onClick={() => window.confirm('Cancel this booking?') && cancelBooking.mutate({ id: booking.id, data: {} })}
              className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-strivre-lightburgundy text-strivre-burgundy hover:bg-strivre-lightburgundy/70"
            >
              <XCircle size={14} /> Cancel Booking
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white border border-strivre-lightnavy rounded-lg p-5">
          <h3 className="text-sm font-bold text-strivre-navy mb-3">Booking Information</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Status</dt><dd><BookingStatusBadge status={booking.status} /></dd></div>
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Date</dt><dd className="text-strivre-darktext font-medium">{new Date(booking.bookingDate).toLocaleDateString()}</dd></div>
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Time</dt><dd className="text-strivre-darktext font-medium">{new Date(booking.bookingDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</dd></div>
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Duration</dt><dd className="text-strivre-darktext font-medium">{durationMinutes} min</dd></div>
            {booking.customerNotes && <div className="flex justify-between gap-4"><dt className="text-strivre-mutedtext">Notes</dt><dd className="text-strivre-darktext text-right">{booking.customerNotes}</dd></div>}
            {booking.adminNotes && <div className="flex justify-between gap-4"><dt className="text-strivre-mutedtext">Admin Notes</dt><dd className="text-strivre-darktext text-right">{booking.adminNotes}</dd></div>}
          </dl>
        </section>

        <section className="bg-white border border-strivre-lightnavy rounded-lg p-5">
          <h3 className="text-sm font-bold text-strivre-navy mb-3">Customer Information</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Name</dt><dd className="text-strivre-darktext font-medium">{booking.customerFirstName} {booking.customerLastName}</dd></div>
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Email</dt><dd className="text-strivre-darktext font-medium">{booking.customerEmail}</dd></div>
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Phone</dt><dd className="text-strivre-darktext font-medium">{booking.customerPhone ?? 'N/A'}</dd></div>
          </dl>
        </section>

        <section className="bg-white border border-strivre-lightnavy rounded-lg p-5">
          <h3 className="text-sm font-bold text-strivre-navy mb-3">Service Information</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Service</dt><dd className="text-strivre-darktext font-medium">{booking.serviceName}</dd></div>
          </dl>
        </section>

        <section className="bg-white border border-strivre-lightnavy rounded-lg p-5">
          <h3 className="text-sm font-bold text-strivre-navy mb-3">Calendar Information</h3>
          <div className="flex justify-between items-center text-sm">
            <span className="text-strivre-mutedtext">Calendar Sync</span>
            <SyncStatusBadge status={booking.calendarSyncStatus} />
          </div>
        </section>

        <section className="bg-white border border-strivre-lightnavy rounded-lg p-5">
          <h3 className="text-sm font-bold text-strivre-navy mb-3">CRM Information</h3>
          <div className="flex justify-between items-center text-sm">
            <span className="text-strivre-mutedtext">CRM Sync</span>
            <SyncStatusBadge status={booking.crmSyncStatus} />
          </div>
        </section>

        <section className="bg-white border border-strivre-lightnavy rounded-lg p-5 lg:col-span-2">
          <h3 className="text-sm font-bold text-strivre-navy mb-3">Audit History</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Created</dt><dd className="text-strivre-darktext font-medium">{new Date(booking.createdDate).toLocaleString()}</dd></div>
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Confirmation Sent</dt><dd className="text-strivre-darktext font-medium">{booking.confirmationSentAt ? new Date(booking.confirmationSentAt).toLocaleString() : 'Not sent'}</dd></div>
            <div className="flex justify-between"><dt className="text-strivre-mutedtext">Last Reminder Sent</dt><dd className="text-strivre-darktext font-medium">{booking.lastReminderSentAt ? new Date(booking.lastReminderSentAt).toLocaleString() : 'None sent'}</dd></div>
          </dl>
        </section>
      </div>

      <BookingModal
        isOpen={rescheduleOpen}
        mode="reschedule"
        booking={booking}
        services={[]}
        saving={rescheduleBooking.isPending}
        onClose={() => setRescheduleOpen(false)}
        onSave={(values) => {
          rescheduleBooking.mutate(
            { id: booking.id, data: { newBookingDate: `${values.bookingDate}T${values.bookingTime}:00` } },
            { onSuccess: () => setRescheduleOpen(false) }
          );
        }}
      />
    </div>
  );
}

